import { forwardRef, useMemo, useRef, useId } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import FormFieldLabel from '../Form/controls/FormFieldLabel';
import { SELECT_MODE } from './constants';
import ListboxFooter from './ListboxFooter';
import { typography } from '../../../theme/tokens/typography';
import {
  defaultGetOptionLabel,
  defaultIsOptionEqualToValue,
  mergeSlotProps,
} from './selectUtils';

const localFilter = createFilterOptions();

function renderGroupedOptions(params) {
  return (
    <Box component="li" key={params.key} sx={{ listStyle: 'none' }}>
      {params.group ? (
        <Box
          sx={{
            position: 'sticky',
            top: -8,
            zIndex: 1,
            px: 1.5,
            pt: 1.25,
            pb: 0.5,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'text.secondary',
            bgcolor: 'background.paper',
            pointerEvents: 'none',
          }}
        >
          {params.group}
        </Box>
      ) : null}
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {params.children}
      </Box>
    </Box>
  );
}

/**
 * Presentational searchable select.
 * - static: parent supplies full options; local filter only
 * - infinite: parent supplies loaded pages; local filter + onLoadMore for scroll
 */
export default function SearchableSelect({
  mode = SELECT_MODE.STATIC,
  options = [],
  label,
  hint,
  placeholder,
  value = null,
  onChange,
  labelKey = 'label',
  valueKey = 'value',
  getOptionLabel,
  isOptionEqualToValue,
  loading = false,
  loadingMore = false,
  disabled = false,
  error = false,
  helperText,
  required = false,
  multiple = false,
  clearable = true,
  onLoadMore,
  onOpen,
  onClose,
  onSearchInputChange,
  filterOptions: filterOptionsProp,
  getOptionDisabled,
  groupBy,
  noOptionsText = 'No options found',
  size = 'small',
  sx,
  slotProps,
  startAdornment,
  className,
  shrinkLabel = false,
  renderOption,
}) {
  const loadMoreLockRef = useRef(false);
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const inputId = useId();
  const isInfinite = mode === SELECT_MODE.INFINITE;
  const themedSx = {
    width: '100%',
    ...sx,
  };

  const resolveLabel = useMemo(
    () =>
      getOptionLabel ||
      ((option) => defaultGetOptionLabel(option, labelKey, valueKey)),
    [getOptionLabel, labelKey, valueKey]
  );

  const resolveEqual = useMemo(
    () =>
      isOptionEqualToValue ||
      ((option, selected) => defaultIsOptionEqualToValue(option, selected, valueKey)),
    [isOptionEqualToValue, valueKey]
  );

  const filterOptions = filterOptionsProp ?? (onSearchInputChange ? (opts) => opts : localFilter);

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === 'input') {
      onSearchInputChange?.(newInputValue);
      if (!multiple && clearable && newInputValue === '' && value != null) {
        onChange?.(null);
      }
      return;
    }

    if (reason === 'clear' || reason === 'reset') {
      onSearchInputChange?.('');
    }
  };

  const handleClose = (event, reason) => {
    onSearchInputChange?.('');
    onClose?.(event, reason);
  };

  const visibleOptions = useMemo(() => {
    if (!multiple) return options;
    const selected = Array.isArray(value) ? value : [];
    return options.filter(
      (option) => !selected.some((item) => resolveEqual(option, item))
    );
  }, [multiple, options, resolveEqual, value]);

  const handleScroll = (event) => {
    if (!isInfinite || !onLoadMore || loading || loadingMore) return;

    const listboxNode = event.currentTarget;
    const nearBottom =
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 8;

    if (!nearBottom || loadMoreLockRef.current) return;

    loadMoreLockRef.current = true;
    onLoadMore();
    window.setTimeout(() => {
      loadMoreLockRef.current = false;
    }, 200);
  };

  const ListboxComponent = useMemo(
    () =>
      forwardRef(function StableListbox(listboxProps, ref) {
        return (
          <ListboxFooter
            {...listboxProps}
            ref={ref}
            loadingMore={loadingMoreRef.current}
          />
        );
      }),
    []
  );

  return (
    <Box sx={{ width: '100%' }} className={className}>
      <FormFieldLabel label={label} required={required} hint={hint} htmlFor={inputId} 
          sx={{
            '& .MuiTypography-root': {
              fontSize: typography.fontSizeXss,
              fontWeight: typography.fontWeightSemibold,
            }
          }} />
      <Autocomplete
        fullWidth
        size={size}
        multiple={multiple}
        autoHighlight={false} 
        disableClearable={!clearable}
        options={visibleOptions}
        value={value ?? (multiple ? [] : null)}
        loading={loading}
        disabled={disabled}
        getOptionLabel={resolveLabel}
        isOptionEqualToValue={resolveEqual}
        getOptionDisabled={getOptionDisabled}
        groupBy={groupBy}
        renderGroup={groupBy ? renderGroupedOptions : undefined}
        filterOptions={filterOptions}
        noOptionsText={loading ? 'Loading options...' : noOptionsText}
        loadingText="Loading options..."
        onOpen={onOpen}
        onClose={handleClose}
        onInputChange={onSearchInputChange ? handleInputChange : undefined}
        onChange={(_event, selectedValue) => onChange?.(selectedValue)}
        slotProps={mergeSlotProps(
          {
            listbox: {
              onScroll: isInfinite ? handleScroll : undefined,
            },
          },
          slotProps
        )}
        ListboxComponent={isInfinite ? ListboxComponent : undefined}
        sx={themedSx}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            id={inputId}
            hiddenLabel
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            slotProps={{
              formHelperText: { sx: { mx: 0, mt: 0.75 } },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
